import { useCallback, useRef } from "react";

export type PersistedListState = Record<string, string | number | boolean | undefined | null>;

export const LIST_STATE_KEYS = {
    webUrls: "webUrlsListState",
    endpoints: "endpointsListState",
    auditLogs: "auditLogsListState",
    platformAuditLogs: "platformAuditLogsListState",
    tags: "tagsListState",
    registeredUsers: "registeredUsersListState",
    inviteUsers: "inviteUsersListState",
    userRoles: "userRolesListState",
    policyProfiles: "policyProfilesListState",
} as const;

export function scopedListStateKey(prefix: string, scope: string) {
    return `${prefix}-${scope}`;
}

export const SCOPED_LIST_STATE_PREFIX = {
    tenantK8: "tenantK8Nav",
    tenantCloudResources: "tenantCloudResources",
    auditLogsDevice: "auditLogsListState-device",
} as const;

export function readPersistedListState<T extends PersistedListState>(
    key: string,
): Partial<T> {
    if (typeof window === "undefined") {
        return {};
    }
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

export function writePersistedListState<T extends PersistedListState>(
    key: string,
    state: Partial<T>,
) {
    if (typeof window === "undefined") {
        return;
    }
    const current = readPersistedListState<T>(key);
    sessionStorage.setItem(key, JSON.stringify({ ...current, ...state }));
}

export function parsePersistedNumber(value: unknown, fallback: number): number {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string" && value.trim()) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return fallback;
}

export function parsePersistedString(value: unknown, fallback = ""): string {
    return typeof value === "string" ? value : fallback;
}

export function parsePersistedNullableString(value: unknown): string | null {
    return typeof value === "string" ? value : null;
}

export function parsePersistedDate(value: unknown): Date | undefined {
    if (typeof value !== "string" || !value.trim()) {
        return undefined;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export function serializePersistedDate(value?: Date): string | undefined {
    return value ? value.toISOString() : undefined;
}

/**
 * Wraps tenant/tab/filter handlers so page resets to 1 only after the first
 * invocation. The first call is skipped to preserve sessionStorage-restored page
 * when TenantLists (or similar) fires on mount after back navigation.
 */
export function useSkipInitialPageReset(setPage: (page: number) => void) {
    const skipRef = useRef(true);

    return useCallback(
        (applyChange: () => void) => {
            applyChange();
            if (skipRef.current) {
                skipRef.current = false;
                return;
            }
            setPage(1);
        },
        [setPage],
    );
}
